// pages/public/SharedForm.jsx
// Handles direct form links: /f/:token
// Works for both assessment forms and feedback forms
// No landing page needed - goes straight to the form

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAssessment } from '../../context/AssessmentContext';
import axios from 'axios';

const SharedForm = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { captureUser, loading } = useAssessment();

  const [form, setForm] = useState(null);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState('details'); // details, feedback, thankyou
  const [userDetails, setUserDetails] = useState({ firstName: '', lastName: '', email: '', jobTitle: '' });
  const [feedbackAnswers, setFeedbackAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [detailsError, setDetailsError] = useState('');

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`/forms/share/${token}`);
      if (response.data.success) {
        setForm(response.data.data.form);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'This link is invalid or no longer active.');
    } finally {
      setValidating(false);
    }
  };

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    setDetailsError('');

    if (!userDetails.firstName || !userDetails.lastName || !userDetails.email) {
      setDetailsError('Please fill in your name and email');
      return;
    }

    if (form.formType === 'assessment') {
      // Capture user and go to assessment
      setSubmitting(true);
      try {
        const result = await captureUser({
          firstName: userDetails.firstName,
          lastName: userDetails.lastName,
          email: userDetails.email,
          jobTitle: userDetails.jobTitle,
          sharedLinkToken: token,
        });
        if (result?.success) {
          navigate('/assessment');
        } else {
          setDetailsError(result?.error || 'Something went wrong. Please try again.');
        }
      } catch (err) {
        setDetailsError('Something went wrong. Please try again.');
      } finally {
        setSubmitting(false);
      }
    } else {
      // Feedback form - go to questions step
      setStep('feedback');
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const answers = form.questions
        .filter(q => q.active)
        .map(q => {
          const qId = q.questionId || q._id;
          let answer = feedbackAnswers[qId];

          // A ranking question that the respondent never reordered has no
          // stored value, but they still saw and implicitly accepted an order.
          // Record the order as displayed rather than submitting nothing.
          if (q.questionType === 'ranking' && !Array.isArray(answer)) {
            answer = q.options || [];
          }

          return {
            questionId: qId,
            questionText: q.questionText,
            questionType: q.questionType,
            answer: answer === undefined || answer === null ? '' : answer,
          };
        });

      await axios.post('/feedback/submit', {
        formId: form._id,
        respondentName: `${userDetails.firstName} ${userDetails.lastName}`,
        respondentEmail: userDetails.email,
        answers,
        sharedLinkToken: token,
      });

      setStep('thankyou');
    } catch (err) {
      setDetailsError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setFeedbackAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const renderQuestion = (q) => {
    const qId = q.questionId || q._id;
    const value = feedbackAnswers[qId] || '';

    switch (q.questionType) {
      case 'multiple_choice':
        return (
          <div style={s.options}>
            {q.options?.map((opt, i) => (
              <label key={i} style={s.optionLabel}>
                <input
                  type="radio"
                  name={qId}
                  value={opt}
                  checked={value === opt}
                  onChange={() => handleAnswerChange(qId, opt)}
                  style={s.radio}
                />
                {opt}
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <div style={s.options}>
            {q.options?.map((opt, i) => {
              const checked = Array.isArray(value) ? value.includes(opt) : false;
              return (
                <label key={i} style={s.optionLabel}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const current = Array.isArray(value) ? value : [];
                      handleAnswerChange(qId, e.target.checked
                        ? [...current, opt]
                        : current.filter(v => v !== opt)
                      );
                    }}
                    style={s.radio}
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        );

      case 'short_text':
        return (
          <input
            style={s.input}
            type="text"
            value={value}
            onChange={e => handleAnswerChange(qId, e.target.value)}
            placeholder="Your answer..."
          />
        );

      case 'long_text':
        return (
          <textarea
            style={s.textarea}
            rows={4}
            value={value}
            onChange={e => handleAnswerChange(qId, e.target.value)}
            placeholder="Your answer..."
          />
        );

      case 'rating': {
        const min = q.minValue || 1;
        const max = q.maxValue || 5;
        const points = max - min + 1;
        // The buttons used to sit in a fixed width flex row while the labels
        // spanned the full container, so the three labels never lined up with
        // points 1, 3 and 5. Both rows are now full width grids over the same
        // number of columns, which makes the centre label land on the centre
        // point at any width.
        const scaleCols = { display: 'grid', gridTemplateColumns: `repeat(${points}, minmax(0, 1fr))`, gap: '8px' };
        // Keep the scale from stretching absurdly wide on desktop while still
        // filling narrow screens.
        const scaleWrap = { maxWidth: `${points * 108}px` };
        return (
          <div style={scaleWrap}>
            <div style={scaleCols}>
              {Array.from({ length: points }, (_, i) => i + min).map(n => (
                <button
                  key={n}
                  type="button"
                  style={{ ...s.ratingBtn, ...(value === n ? s.ratingBtnActive : {}) }}
                  onClick={() => handleAnswerChange(qId, n)}
                >
                  {n}
                </button>
              ))}
            </div>
            {q.midLabel ? (
              // Three labels: one at each end and one at the midpoint, matching
              // the SurveyMonkey layout Steve supplied.
              <div style={{ ...scaleCols, marginTop: '10px', alignItems: 'flex-start' }}>
                {Array.from({ length: points }, (_, i) => {
                  // Labels sit under points 1, mid and last. Same grid as the
                  // buttons, so each label is centred on its own point.
                  const isFirst = i === 0;
                  const isMid = i === Math.floor(points / 2);
                  const isLast = i === points - 1;
                  const text = isFirst ? q.minLabel : isMid ? q.midLabel : isLast ? q.maxLabel : '';
                  if (!text) return <span key={i} />;
                  return (
                    <span
                      key={i}
                      style={{
                        ...s.ratingLabelMid,
                        textAlign: isFirst ? 'left' : isLast ? 'right' : 'center',
                        whiteSpace: 'normal',
                      }}
                    >
                      {text}
                    </span>
                  );
                })}
              </div>
            ) : (
              <div style={{ ...s.ratingLabels, marginTop: '10px' }}>
                <span>{q.minLabel}</span>
                <span>{q.maxLabel}</span>
              </div>
            )}
          </div>
        );
      }

      case 'nps':
        // 11 buttons in a wrapping flex row left "10" orphaned on its own line.
        // A grid of 11 equal columns keeps the whole scale on one line and puts
        // the end labels directly under 0 and 10.
        return (
          <div>
            <div style={s.npsRow}>
              {Array.from({ length: 11 }, (_, i) => i).map(n => (
                <button
                  key={n}
                  type="button"
                  style={{ ...s.npsBtn, ...(value === n ? s.npsBtnActive : {}) }}
                  onClick={() => handleAnswerChange(qId, n)}
                >
                  {n}
                </button>
              ))}
            </div>
            <div style={s.ratingLabels}>
              <span>{q.minLabel || 'Not at all likely'}</span>
              <span>{q.maxLabel || 'Extremely likely'}</span>
            </div>
          </div>
        );

      case 'yes_no':
        return (
          <div style={s.yesNoRow}>
            {['Yes', 'No'].map(opt => (
              <button
                key={opt}
                type="button"
                style={{ ...s.yesNoBtn, ...(value === opt ? s.yesNoBtnActive : {}) }}
                onClick={() => handleAnswerChange(qId, opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        );

      case 'dropdown':
        return (
          <select
            style={s.select}
            value={value}
            onChange={e => handleAnswerChange(qId, e.target.value)}
          >
            <option value="">Select an option...</option>
            {q.options?.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'bipolar':
        return (
          <div>
            <div style={s.bipolarRow}>
              <span style={s.bipolarLabelLeft}>{q.minLabel}</span>
              <div style={s.bipolarBtns}>
                {[1, 2, 3, 4, 5, 6, 7].map(n => (
                  <button
                    key={n}
                    type="button"
                    style={{ ...s.bipolarBtn, ...(value === n ? s.bipolarBtnActive : {}) }}
                    onClick={() => handleAnswerChange(qId, n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <span style={s.bipolarLabelRight}>{q.maxLabel}</span>
            </div>
          </div>
        );

      case 'ranking': {
        // The styles for this (rankingContainer, rankingItem, rankingNum,
        // rankingBtns, rankingBtn) were already written but no case ever used
        // them, so ranking questions fell through to the default text input.
        //
        // Value is the ordered array of option labels. Until the respondent
        // moves anything, that is simply the order the options were defined in.
        const order = Array.isArray(value) && value.length === (q.options?.length || 0)
          ? value
          : (q.options || []);

        const move = (from, to) => {
          if (to < 0 || to >= order.length) return;
          const next = [...order];
          const [item] = next.splice(from, 1);
          next.splice(to, 0, item);
          handleAnswerChange(qId, next);
        };

        return (
          <div style={s.rankingContainer}>
            <p style={s.rankingHint}>
              Use the arrows to order these from most important (1) to least important.
            </p>
            {order.map((opt, i) => (
              <div key={opt} style={s.rankingItem}>
                <span style={s.rankingNum}>{i + 1}</span>
                <span style={s.rankingText}>{opt}</span>
                <span style={s.rankingBtns}>
                  <button
                    type="button"
                    style={{ ...s.rankingBtn, ...(i === 0 ? s.rankingBtnDisabled : {}) }}
                    onClick={() => move(i, i - 1)}
                    disabled={i === 0}
                    aria-label={`Move ${opt} up`}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    style={{ ...s.rankingBtn, ...(i === order.length - 1 ? s.rankingBtnDisabled : {}) }}
                    onClick={() => move(i, i + 1)}
                    disabled={i === order.length - 1}
                    aria-label={`Move ${opt} down`}
                  >
                    ▼
                  </button>
                </span>
              </div>
            ))}
          </div>
        );
      }

      default:
        return (
          <input
            style={s.input}
            type="text"
            value={value}
            onChange={e => handleAnswerChange(qId, e.target.value)}
            placeholder="Your answer..."
          />
        );
    }
  };

  if (validating) {
    return (
      <div style={s.loadingPage}>
        <div style={s.spinner} />
        <p style={s.loadingText}>Loading form...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.page}>
        <div style={s.header}>
          <div style={s.logo}>
            <span style={s.logoExec}>EXECUTIVE</span>
            <span style={s.logoSpring}>Springboard</span>
          </div>
        </div>
        <div style={s.errorContainer}>
          <div style={s.errorIcon}>❌</div>
          <h1 style={s.errorTitle}>Form Not Available</h1>
          <p style={s.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.logo}>
          <span style={s.logoExec}>EXECUTIVE</span>
          <span style={s.logoSpring}>Springboard</span>
        </div>
      </div>

      <div style={s.container} className="esb-container">

        {/* STEP: User Details */}
        {step === 'details' && (
          <div style={s.card} className="esb-card-lg">
            <h1 style={s.title}>{form.name}</h1>
            {form.description && <p style={s.desc}>{form.description}</p>}

            <div style={s.divider} />

            <h2 style={s.subtitle}>Your Details</h2>
            <p style={s.subtitleDesc}>Please enter your details to get started.</p>

            {detailsError && <div style={s.errorBox}>{detailsError}</div>}

            <form onSubmit={handleDetailsSubmit}>
              <div style={s.row} className="esb-grid-2">
                <div style={s.formGroup}>
                  <label style={s.label}>First Name *</label>
                  <input
                    style={s.input}
                    type="text"
                    value={userDetails.firstName}
                    onChange={e => setUserDetails(p => ({ ...p, firstName: e.target.value }))}
                    disabled={submitting}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Last Name *</label>
                  <input
                    style={s.input}
                    type="text"
                    value={userDetails.lastName}
                    onChange={e => setUserDetails(p => ({ ...p, lastName: e.target.value }))}
                    disabled={submitting}
                  />
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Email Address *</label>
                <input
                  style={s.input}
                  type="email"
                  value={userDetails.email}
                  onChange={e => setUserDetails(p => ({ ...p, email: e.target.value }))}
                  disabled={submitting}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Job Title <span style={s.optional}>(optional)</span></label>
                <input
                  style={s.input}
                  type="text"
                  value={userDetails.jobTitle}
                  onChange={e => setUserDetails(p => ({ ...p, jobTitle: e.target.value }))}
                  disabled={submitting}
                />
              </div>
              <button type="submit" style={s.btnSubmit} disabled={submitting || loading}>
                {submitting ? 'Please wait...' : form.formType === 'assessment' ? 'Start Assessment →' : 'Continue →'}
              </button>
            </form>
          </div>
        )}

        {/* STEP: Feedback Questions */}
        {step === 'feedback' && (
          <div style={s.card} className="esb-card-lg">
            <h1 style={s.title}>{form.name}</h1>
            <p style={s.desc}>Please answer the questions below. Fields marked * are required.</p>
            <div style={s.divider} />

            {detailsError && <div style={s.errorBox}>{detailsError}</div>}

            <form onSubmit={handleFeedbackSubmit}>
              {form.questions?.filter(q => q.active).map((q, idx) => (
                <div key={q._id || idx} style={s.questionBlock}>
                  <div style={s.questionText}>
                    <span style={s.questionNum}>{idx + 1}.</span>
                    {q.questionText}
                    {q.required && <span style={s.requiredStar}> *</span>}
                  </div>
                  {q.helpText && <p style={s.helpText}>{q.helpText}</p>}
                  {renderQuestion(q)}
                </div>
              ))}

              <div style={s.submitRow}>
                <button type="button" style={s.btnBack} onClick={() => setStep('details')}>
                  ← Back
                </button>
                <button type="submit" style={s.btnSubmit} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit →'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* STEP: Thank You */}
        {step === 'thankyou' && (
          <div style={s.thankYouCard}>
            <div style={s.thankYouIcon}>🎉</div>
            <h1 style={s.thankYouTitle}>Thank You!</h1>
            <p style={s.thankYouText}>
              Your response has been submitted successfully. We appreciate your feedback.
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

const s = {
  page: { minHeight: '100vh', backgroundColor: 'var(--color-off-white)', fontFamily: 'var(--font-primary)' },
  loadingPage: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' },
  spinner: { width: '40px', height: '40px', border: '4px solid var(--color-light-gray)', borderTopColor: 'var(--color-teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingText: { fontSize: '15px', color: 'var(--color-text-light)' },
  header: { backgroundColor: 'var(--color-primary)', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center' },
  logo: { display: 'flex', flexDirection: 'column', gap: '1px' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#FFFFFF', lineHeight: 1 },
  container: { maxWidth: '600px', margin: '0 auto', padding: '40px 24px 80px' },
  card: { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-md)', padding: '40px' },
  title: { fontSize: '24px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '8px' },
  desc: { fontSize: '15px', color: 'var(--color-text-light)', lineHeight: '1.6', marginBottom: '0' },
  divider: { borderTop: '1px solid var(--color-border)', margin: '24px 0' },
  subtitle: { fontSize: '18px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '4px' },
  subtitleDesc: { fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '20px' },
  errorBox: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', border: '1px solid #F5C6CB', borderRadius: '8px', padding: '12px 16px', fontSize: '14px', marginBottom: '16px' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--color-text)', marginBottom: '6px' },
  optional: { fontWeight: '400', color: 'var(--color-text-light)' },
  input: { width: '100%', padding: '11px 14px', border: '1.5px solid var(--color-border)', borderRadius: '8px', fontSize: '14px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '11px 14px', border: '1.5px solid var(--color-border)', borderRadius: '8px', fontSize: '14px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box', resize: 'vertical' },
  select: { width: '100%', padding: '11px 14px', border: '1.5px solid var(--color-border)', borderRadius: '8px', fontSize: '14px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box', backgroundColor: '#fff' },
  btnSubmit: { width: '100%', backgroundColor: 'var(--color-teal)', color: '#fff', padding: '14px', borderRadius: '8px', border: 'none', fontSize: '16px', fontWeight: '600', cursor: 'pointer', marginTop: '8px' },
  questionBlock: { marginBottom: '28px', paddingBottom: '28px', borderBottom: '1px solid var(--color-border)' },
  questionText: { fontSize: '15px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '12px', lineHeight: '1.5' },
  questionNum: { color: 'var(--color-teal)', marginRight: '6px' },
  requiredStar: { color: 'var(--color-error)' },
  helpText: { fontSize: '13px', color: 'var(--color-text-light)', marginBottom: '10px', fontStyle: 'italic' },
  options: { display: 'flex', flexDirection: 'column', gap: '10px' },
  optionLabel: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: 'var(--color-text)', cursor: 'pointer' },
  radio: { width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 },
  ratingRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  ratingBtn: { width: '100%', minWidth: 0, height: '44px', borderRadius: '8px', border: '1.5px solid var(--color-border)', backgroundColor: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer', color: 'var(--color-text)' },
  ratingBtnActive: { backgroundColor: 'var(--color-teal)', color: '#fff', border: '1.5px solid var(--color-teal)' },
  ratingLabels: { display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: 'var(--color-text-light)' },
  npsRow: { display: 'grid', gridTemplateColumns: 'repeat(11, minmax(0, 1fr))', gap: '6px' },
  npsBtn: { width: '100%', minWidth: 0, height: '40px', borderRadius: '6px', border: '1.5px solid var(--color-border)', backgroundColor: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer', color: 'var(--color-text)' },
  npsBtnActive: { backgroundColor: 'var(--color-primary)', color: '#fff', border: '1.5px solid var(--color-primary)' },
  yesNoRow: { display: 'flex', gap: '12px' },
  yesNoBtn: { padding: '10px 32px', borderRadius: '8px', border: '1.5px solid var(--color-border)', backgroundColor: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer', color: 'var(--color-text)' },
  yesNoBtnActive: { backgroundColor: 'var(--color-teal)', color: '#fff', border: '1.5px solid var(--color-teal)' },
  bipolarRow: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap' },
  bipolarBtns: { display: 'flex', gap: '6px' },
  bipolarLabelLeft: { fontSize: '12px', color: 'var(--color-text-light)', minWidth: '80px', textAlign: 'right', flexShrink: 0 },
  bipolarLabelRight: { fontSize: '12px', color: 'var(--color-text-light)', minWidth: '80px', textAlign: 'left', flexShrink: 0 },
  bipolarBtn: { width: '40px', height: '40px', borderRadius: '6px', border: '1.5px solid var(--color-border)', backgroundColor: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer', color: 'var(--color-text)' },
  bipolarBtnActive: { backgroundColor: 'var(--color-primary)', color: '#fff', border: '1.5px solid var(--color-primary)' },
  ratingLabelsTriple: { display: 'grid', gridTemplateColumns: '1fr 1.2fr 1fr', alignItems: 'flex-start', gap: '10px', marginTop: '10px' },
  ratingLabelEnd: { fontSize: '12px', color: 'var(--color-text-light)', textAlign: 'left', lineHeight: '1.4' },
  ratingLabelMid: { fontSize: '12px', color: 'var(--color-text-light)', textAlign: 'center', lineHeight: '1.4' },
  ratingLabelEndRight: { fontSize: '12px', color: 'var(--color-text-light)', textAlign: 'right', lineHeight: '1.4' },
  rankingBtnDisabled: { opacity: 0.35, cursor: 'not-allowed' },
  rankingContainer: { display: 'flex', flexDirection: 'column', gap: '8px' },
  rankingHint: { fontSize: '12px', color: 'var(--color-text-light)', marginBottom: '8px', fontStyle: 'italic' },
  rankingItem: { display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: 'var(--color-off-white)', borderRadius: '8px', padding: '10px 14px', border: '1px solid var(--color-border)' },
  rankingNum: { fontSize: '13px', fontWeight: '700', color: 'var(--color-teal)', minWidth: '20px' },
  rankingText: { fontSize: '14px', color: 'var(--color-text)', flex: 1 },
  rankingBtns: { display: 'flex', gap: '4px' },
  rankingBtn: { width: '28px', height: '28px', borderRadius: '5px', border: '1px solid var(--color-border)', backgroundColor: '#fff', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  submitRow: { display: 'flex', gap: '12px', marginTop: '24px' },
  btnBack: { backgroundColor: 'var(--color-off-white)', color: 'var(--color-text)', padding: '14px 24px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '15px', fontWeight: '500', cursor: 'pointer' },
  thankYouCard: { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-md)', padding: '60px 40px', textAlign: 'center' },
  thankYouIcon: { fontSize: '56px', marginBottom: '20px' },
  thankYouTitle: { fontSize: '28px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '12px' },
  thankYouText: { fontSize: '16px', color: 'var(--color-text-light)', lineHeight: '1.7', maxWidth: '400px', margin: '0 auto' },
  errorContainer: { maxWidth: '480px', margin: '80px auto', textAlign: 'center', padding: '24px' },
  errorIcon: { fontSize: '48px', marginBottom: '16px' },
  errorTitle: { fontSize: '24px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '12px' },
  errorText: { fontSize: '15px', color: 'var(--color-text-light)' },
};

export default SharedForm;
